import { localiseTerms, t } from './markets'
import { formatMoney } from './format-money'
import { getPtModifiers } from './pt-package-price'

// Display labels for the UAE / Malaysia booking flow.
//
// The values behind these labels are backend keys and never change: aircon
// item names ("General Servicing", "Gas Refill Half Tank"), mount keys
// ("Wall" / "Ceiling" / "Ducted") and the question choices in
// REQUEST_CATEGORY_SETTINGS (e.g. "Condo Gym", "Female") are parsed by the
// pricing calculator, trainer matching and the female-trainer premium. Only
// what the customer reads - and the criteria text ops reads - is localised.
// Singapore (or no market) always gets the SG text back.

const isLocal = (market) => market && market.countryCd !== 'sg'

// ---- Aircon picker items (utils/items-selection.js names) --------------------
const ITEM_LABELS = {
    ae: {
        'Aircon Repair (est.)': ['AC Repair', 'Repair'],
        'General Servicing': ['AC Cleaning', 'Cleaning'],
        'Chemical Wash': ['Coil Cleaning (Chemical Wash)', 'Coil Clean'],
        'Chemical Overhaul': ['Deep Cleaning (Full Dismantle)', 'Deep Clean'],
        'Gas Refill Full Tank': ['Gas Refill (Full)', 'Gas Full'],
        'Gas Refill Half Tank': ['Gas Top-up', 'Gas Top-up'],
        'Condenser Cleaning': ['Outdoor Unit Cleaning', 'Outdoor Unit'],
        'Condenser Chem. Wash': ['Outdoor Unit Coil Wash', 'Outdoor Wash'],
        'Drain Pipe Flush': ['Drain Line Flush', 'Drain Flush'],
    },
    my: {
        'Aircon Repair (est.)': ['Aircond Repair', 'Repair'],
        'General Servicing': ['General Service', 'General'],
        'Chemical Wash': ['Chemical Wash', 'Chem Wash'],
        'Chemical Overhaul': ['Chemical Overhaul', 'Overhaul'],
        'Gas Refill Full Tank': ['Gas Refill (Full)', 'Gas Full'],
        'Gas Refill Half Tank': ['Gas Top Up', 'Gas Top Up'],
        'Condenser Cleaning': ['Compressor Cleaning', 'Compressor'],
        'Condenser Chem. Wash': ['Compressor Chemical Wash', 'Comp. Wash'],
        'Drain Pipe Flush': ['Drain Pipe Flush', 'Drain Flush'],
    },
}

export const itemLabel = (itemName, market) =>
    (isLocal(market) && ITEM_LABELS[market.countryCd]?.[itemName]?.[0]) || null
export const shortItemLabel = (itemName, market) =>
    (isLocal(market) && ITEM_LABELS[market.countryCd]?.[itemName]?.[1]) || null

const MOUNT_TERM = { Wall: 'mountWall', Ceiling: 'mountCeiling', Ducted: 'mountDucted' }
export const mountLabel = (mountKey, market) =>
    isLocal(market) && MOUNT_TERM[mountKey] ? t(market, MOUNT_TERM[mountKey]) : mountKey

// One-word mount names for tight spots - the mount dropdown on a selected
// service card is sized to its text and sits next to the price, so the full
// terms ("Ducted (concealed)") pushed the price out of view.
const SHORT_MOUNT = {
    ae: { Wall: 'Split', Ceiling: 'Cassette', Ducted: 'Ducted' },
    my: { Wall: 'Wall', Ceiling: 'Cassette', Ducted: 'Ducted' },
}
export const shortMountLabel = (mountKey, market) =>
    (isLocal(market) && SHORT_MOUNT[market.countryCd]?.[mountKey]) || mountKey

// ---- Booking questions (REQUEST_CATEGORY_SETTINGS criteria) -------------------
// Per market, per request type, per question code:
//   question: replacement question text
//   choices: { value: label }
//   extraChoices: choices the market offers in addition (still backend keys)
const CRITERIA_OVERLAY = {
    ae: {
        'Fitness Trainer': {
            5: { choices: { Home: 'Home (villa or apartment)', 'Condo Gym': 'Building or Compound Gym', Outdoor: 'Outdoor (park or beach)', 'Public Gym': 'Commercial Gym' } },
        },
        'Aircon Services': {
            24: {
                question: 'What type of AC unit is it?',
                // Concealed ducted units are the norm in UAE apartments; the
                // calculator already prices mount key "Ducted".
                extraChoices: [{ value: 'Ducted', next_question_code: '25' }],
            },
        },
    },
    my: {
        'Fitness Trainer': {
            5: { choices: { 'Public Gym': 'Gym', Outdoor: 'Outdoor / Taman' } },
        },
        'Aircon Services': {
            24: { question: 'Is your aircond wall-mounted or a ceiling cassette?' },
        },
    },
}

const choiceLabel = (requestType, code, value, market) => {
    const overlay = CRITERIA_OVERLAY[market.countryCd]?.[requestType]?.[code]
    if (overlay?.choices?.[value]) return overlay.choices[value]
    if (requestType === 'Aircon Services' && code === '24') return mountLabel(value, market)
    return localiseTerms(value, market)
}

/**
 * A copy of the country's REQUEST_CATEGORY_SETTINGS with localised question
 * text and a display `label` on every choice (`value` untouched), plus any
 * extra choices the market offers. Singapore settings are returned as is.
 */
export const localiseRequestSettings = (settings, market) => {
    if (!isLocal(market) || !Array.isArray(settings)) return settings
    return settings.map((setting) => {
        const questions = setting.criteriaQuestions
        if (!Array.isArray(questions)) return setting
        return {
            ...setting,
            headline: localiseTerms(setting.headline, market),
            description: localiseTerms(setting.description, market),
            criteriaQuestions: questions.map((q) => {
                const overlay = CRITERIA_OVERLAY[market.countryCd]?.[setting.requestType]?.[q.question_code]
                const choices = [...(q.choices ?? []), ...(overlay?.extraChoices ?? [])]
                return {
                    ...q,
                    question: overlay?.question ?? localiseTerms(q.question, market),
                    choices: choices.map((c) => ({
                        ...c,
                        label: choiceLabel(setting.requestType, q.question_code, c.value, market),
                        ...(setting.requestType === 'Fitness Trainer' && q.question_code === '4' && c.value === 'Female'
                            ? { note: femaleTrainerNote(market) }
                            : {}),
                    })),
                }
            }),
        }
    })
}

// Female-trainer note on the trainer-preference question, in local currency.
export const femaleTrainerNote = (market) =>
    isLocal(market)
        ? `limited & extra ${formatMoney(getPtModifiers(market.countryCd).female, market.countryCd, 0)}/session`
        : null

